// Store references to our event handlers so we can remove them.
/** @type {Record<string, (event: Event) => void>} */
const trapFocusHandlers = {};

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter(canReceiveFocus);
}

/**
 * AP: a selector match is not a focusable element. `querySelectorAll` cannot
 * see that an element sits under an `inert` or `hidden` ancestor, or is not
 * rendered at all, and `.focus()` on such an element is a silent no-op. A trap
 * built from the raw list can therefore pick a `first` / `last` that never
 * takes focus - the mobile menu's inert submenu panels made Shift+Tab from the
 * first control a dead key.
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function canReceiveFocus(element) {
  if (element.closest('[inert], [hidden]')) return false;

  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility({ visibilityProperty: true });
  }

  return element.getClientRects().length > 0;
}

/**
 * Trap focus within the given container.
 * @param {HTMLElement} container - The container to trap focus within.
 */
export function trapFocus(container) {
  // Clean up any previously set traps.
  removeTrapFocus();

  // Gather focusable elements.
  const focusable = getFocusableElements(container);
  if (!focusable.length) {
    // If nothing is focusable, just abort—no need to trap.
    return;
  }

  // AP: the wrap points are read fresh on every use rather than cached from
  // the list above. Controls come and go while a trap is armed - the country
  // search reveals its reset button on the first keystroke - and a cached
  // `last` would wrap past them. One query per Tab press is negligible.
  const getBounds = () => {
    const elements = getFocusableElements(container);
    return { first: elements[0], last: elements[elements.length - 1] };
  };

  // Keydown handler for cycling focus with Tab and Shift+Tab
  /** @type {(event: KeyboardEvent) => void} */
  trapFocusHandlers.keydown = (event) => {
    if (event.key !== 'Tab') return;

    const activeEl = document.activeElement;
    const { first, last } = getBounds();

    // If on the last focusable and tabbing forward, go to first
    if (!event.shiftKey && activeEl === last) {
      event.preventDefault();
      first?.focus();
    }
    // If on the first (or the container) and shift-tabbing, go to last
    else if (event.shiftKey && (activeEl === first || activeEl === container)) {
      event.preventDefault();
      last?.focus();
    }
  };

  // Focusin (capturing) handler to forcibly keep focus in the container
  /** @type {(event: FocusEvent) => void} */
  trapFocusHandlers.focusin = (event) => {
    // If the newly focused element isn't inside the container, redirect focus back.
    if (event.target instanceof Node && !container.contains(event.target)) {
      event.stopPropagation();
      // E.g., refocus the first focusable element:
      getBounds().first?.focus();
    }
  };

  // Attach the handlers
  document.addEventListener('keydown', trapFocusHandlers.keydown, true);
  // Use capture phase for focusin so we can catch it before it lands outside
  document.addEventListener('focusin', trapFocusHandlers.focusin, true);

  // Finally, put focus where you want it.
  container.focus();
}

/**
 * Remove focus trap and optionally refocus another element.
 */
export function removeTrapFocus() {
  trapFocusHandlers.keydown && document.removeEventListener('keydown', trapFocusHandlers.keydown, true);
  trapFocusHandlers.focusin && document.removeEventListener('focusin', trapFocusHandlers.focusin, true);

  // AP: drop the closures too - they hold the trapped container (and its
  // first/last controls), so a drawer removed from the document would
  // otherwise stay reachable from this module until the next trap.
  delete trapFocusHandlers.keydown;
  delete trapFocusHandlers.focusin;
}

/**
 * AP: focus an element and withhold its focus ring. Browsers show `:focus-visible` on
 * script-moved focus whenever the last user input was a key - and Escape is a key -
 * so a drawer opened by tap and dismissed with Escape rang its trigger, and WebKit
 * rings script-moved focus after a tap regardless (the mobile menu button after a
 * menu-to-drawer handoff). The ring is a keyboard affordance, so callers gate on the
 * interaction that opened the flow (`lastInteractionWasKeyboard()` in dialog.js) and
 * only call this for pointer-driven flows. `data-ap-quiet-focus` (base.css) is dropped
 * on blur or on the next keydown, so keyboard navigation from that point shows focus
 * as usual.
 *
 * @param {HTMLElement} element
 */
export function focusQuietly(element) {
  const clear = () => {
    element.removeAttribute('data-ap-quiet-focus');
    element.removeEventListener('blur', clear);
    document.removeEventListener('keydown', clear, true);
  };

  element.setAttribute('data-ap-quiet-focus', '');
  element.addEventListener('blur', clear);
  document.addEventListener('keydown', clear, true);
  element.focus({ preventScroll: true });
}

/**
 * Cycle focus to the next or previous link
 *
 * @param {HTMLElement[]} items
 * @param {number} increment
 */
export function cycleFocus(items, increment) {
  const currentIndex = items.findIndex((item) => item.matches(':focus'));
  let targetIndex = currentIndex + increment;

  if (targetIndex >= items.length) {
    targetIndex = 0;
  } else if (targetIndex < 0) {
    targetIndex = items.length - 1;
  }

  const targetItem = items[targetIndex];

  if (!targetItem) return;

  targetItem.focus();
}
